/**
 * Centralized Logger Utility
 *
 * Provides consistent logging across the application:
 * - Development: All logs are visible in the console
 * - Production: Only errors are logged
 *
 * Usage:
 *   import { logger } from '@/lib/logger'
 *   logger.log('Debug info', { data })
 *   logger.error('Critical error', error)
 */

const isDev = import.meta.env.DEV

export const logger = {
  /**
   * General logging - development only
   */
  log: (...args: unknown[]) => {
    if (isDev) console.log(...args)
  },

  /**
   * Informational messages - development only
   */
  info: (...args: unknown[]) => {
    if (isDev) console.info(...args)
  },

  /**
   * Warning messages - development only
   */
  warn: (...args: unknown[]) => {
    if (isDev) console.warn(...args)
  },

  /**
   * Debug messages - development only
   */
  debug: (...args: unknown[]) => {
    if (isDev) console.debug(...args)
  },

  /**
   * Error messages - ALWAYS logged (both dev and production)
   * Use for actual errors that need attention
   */
  error: console.error.bind(console),

  /**
   * Group logging for complex operations - development only
   */
  group: (label: string) => {
    if (isDev) console.group(label)
  },

  /**
   * End a logging group - development only
   */
  groupEnd: () => {
    if (isDev) console.groupEnd()
  },

  /**
   * Table logging for arrays/objects - development only
   */
  table: (data: unknown) => {
    if (isDev) console.table(data)
  },

  /**
   * Time tracking - development only
   */
  time: (label: string) => {
    if (isDev) console.time(label)
  },

  /**
   * End time tracking - development only
   */
  timeEnd: (label: string) => {
    if (isDev) console.timeEnd(label)
  },
}

export default logger
